import React from 'react';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { verseService, BiblePassage } from '../../api/verseService';
import Card from '../UI/Card';
import { theme } from '../../styles/theme';
import { AxiosResponse } from 'axios';

const VerseContainer = styled(Card)`
  margin-bottom: ${theme.spacing.lg};
`;

const VerseHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.md};
`;

const VerseTitle = styled.h2`
  margin: 0;
  font-size: ${theme.fontSizes.large};
`;

const VerseDate = styled.span`
  font-size: ${theme.fontSizes.small};
  color: ${theme.colors.gray};
`;

const VerseText = styled.p`
  font-size: ${theme.fontSizes.medium};
  line-height: 1.6;
  margin-bottom: ${theme.spacing.md};
`;

const VerseReference = styled.p`
  font-size: ${theme.fontSizes.small};
  font-weight: 600;
  color: ${theme.colors.primary};
  text-align: right;
`;

const LoadingContainer = styled.div`
  padding: ${theme.spacing.lg};
  text-align: center;
  color: ${theme.colors.gray};
`;

const ErrorContainer = styled.div`
  padding: ${theme.spacing.md};
  color: ${theme.colors.danger};
  text-align: center;
`;

const stripHtml = (html: string) => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  return tempDiv.textContent || tempDiv.innerText || '';
};

const VerseOfTheDay: React.FC = () => {
  const { data, isLoading, error } = useQuery<AxiosResponse<BiblePassage>, Error>({
    queryKey: ['verseOfTheDay'],
    queryFn: () => verseService.getVerseOfTheDay(),
  });

  if (isLoading) {
    return (
      <VerseContainer>
        <LoadingContainer>Loading today's verse...</LoadingContainer>
      </VerseContainer>
    );
  }

  if (error || !data) {
    return (
      <VerseContainer>
        <ErrorContainer>
          Unable to load verse of the day. Please try again later.
        </ErrorContainer>
      </VerseContainer>
    );
  }

  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  
  // Get passage from response
  const passage = data?.data;
  
  if (!passage) {
    return (
      <VerseContainer>
        <ErrorContainer>
          Unable to load verse of the day. Please try again later.
        </ErrorContainer>
      </VerseContainer>
    );
  }
  
  // Get the verse text by stripping HTML
  const verseText = stripHtml(passage.passageHtml);
  
  // Create reference from book and chapter
  const reference = `${passage.book} ${passage.chapter}`;
  const version = "ESV";
  
  return (
    <VerseContainer>
      <VerseHeader>
        <VerseTitle>Verse of the Day</VerseTitle>
        <VerseDate>{today}</VerseDate>
      </VerseHeader>
      <VerseText>"{verseText}"</VerseText>
      <VerseReference>
        {reference} ({version})
      </VerseReference>
    </VerseContainer>
  );
};

export default VerseOfTheDay; 